import React, {useEffect, useMemo, useState} from 'react';
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
    faAnglesLeft,
    faAnglesRight,
    faCalendarDays,
    faChevronLeft,
    faChevronRight,
    faCompass,
    faCrow,
    faSignsPost
} from "@fortawesome/free-solid-svg-icons";
import {pluralize} from "../../helpers/helpers";
import {ExplorerHero, ExplorerHeroStat, ExplorerHeroStats} from "../../components/explorer-hero";
import {ChecklistSearch} from "../../components/checklist-search";
import {HOTSPOT_EXPLORER_PAGE_SIZE} from "./constants";
import {getHotspotExplorerHeroImage} from "./helpers";
import type {HotspotExplorerGroup, HotspotExplorerHotspot, HotspotExplorerSortMode, HotspotExplorerStats} from "./types";
import {HotspotTable} from "./hotspot-table";
import {getHotspotExplorerPageSlice} from "./helpers";
import './hotspot-explorer-shell.scss';

type HotspotExplorerShellProps = {
    eyebrow: string,
    title: string,
    intro: string,
    backgroundImage?: string,
    backLabel?: string,
    onBack?: () => void,
    stats: HotspotExplorerStats,
    childStatLabel?: string,
    showChildStat?: boolean,
    showYearsExploring?: boolean,
    panelHeading: string,
    groups: HotspotExplorerGroup[],
    activeGroupCode?: string,
    onSelectGroup: (code: string) => void,
    hotspots: HotspotExplorerHotspot[],
    sortMode: HotspotExplorerSortMode,
    onChangeSort: (sortMode: HotspotExplorerSortMode) => void,
    currentPage: number,
    onPageChange: (page: number) => void,
    onOpenHotspot: (locationId: string) => void
}

export function HotspotExplorerShell({
    eyebrow,
    title,
    intro,
    backgroundImage,
    backLabel,
    onBack,
    stats,
    childStatLabel,
    showChildStat,
    showYearsExploring: showYearsExploringProp,
    panelHeading,
    groups,
    activeGroupCode,
    onSelectGroup,
    hotspots,
    sortMode,
    onChangeSort,
    currentPage,
    onPageChange,
    onOpenHotspot
}: HotspotExplorerShellProps) {
    const heroImage = useMemo(() => backgroundImage ?? getHotspotExplorerHeroImage(), [backgroundImage]);
    const childStatVisible = showChildStat ?? true;
    const showYearsExploring = showYearsExploringProp ?? true;
    const [searchTerm, setSearchTerm] = useState('');
    const filteredHotspots = useMemo(() => {
        const normalizedSearch = searchTerm.trim().toLowerCase();

        if (!normalizedSearch) return hotspots;

        return hotspots.filter(hotspot => {
            return [
                hotspot.location,
                hotspot.countyLabel,
                hotspot.stateLabel,
                hotspot.countryLabel,
                hotspot.visits.toString(),
                hotspot.speciesSeen.toString(),
                hotspot.lastVisited
            ].some(value => value.toLowerCase().includes(normalizedSearch));
        });
    }, [hotspots, searchTerm]);
    const hotspotPage = useMemo(
        () => getHotspotExplorerPageSlice(filteredHotspots, currentPage, HOTSPOT_EXPLORER_PAGE_SIZE),
        [currentPage, filteredHotspots]
    );

    useEffect(() => {
        if (hotspotPage.currentPage !== currentPage) {
            onPageChange(hotspotPage.currentPage);
        }
    }, [currentPage, hotspotPage.currentPage, onPageChange]);

    const startItem = filteredHotspots.length ? hotspotPage.startIndex + 1 : 0;
    const endItem = hotspotPage.endIndex;
    const totalPages = hotspotPage.totalPages;
    const showPaginationControls = totalPages > 1;

    return (
        <section className="hotspot-explorer">
            <ExplorerHero
                className="explorer-hero--hotspot"
                backgroundImage={heroImage}
                backLabel={backLabel}
                onBack={onBack}
                eyebrow={eyebrow}
                title={title}
                intro={intro}
            >
                <ExplorerHeroStats ariaLabel="Hotspot summary">
                    {childStatVisible ? (
                        <ExplorerHeroStat
                            icon={<FontAwesomeIcon icon={faSignsPost} />}
                            label={childStatLabel ?? stats.childLabel}
                            value={stats.childCount.toLocaleString()}
                        />
                    ) : null}
                    <ExplorerHeroStat
                        icon={<FontAwesomeIcon icon={faCompass} />}
                        label={pluralize(hotspots.length, 'Hotspot Visited', 'Hotspots Visited')}
                        value={hotspots.length.toLocaleString()}
                    />
                    <ExplorerHeroStat
                        icon={<FontAwesomeIcon icon={faCrow} />}
                        label="Species Seen"
                        value={stats.speciesSeen.toLocaleString()}
                    />
                    {showYearsExploring ?
                        <ExplorerHeroStat
                            icon={<FontAwesomeIcon icon={faCalendarDays} />}
                            label={pluralize(stats.yearsExploring, 'Different Year', 'Different Years')}
                            value={stats.yearsExploring.toLocaleString()}
                        />
                        : null
                    }
                </ExplorerHeroStats>
            </ExplorerHero>

            <div className="hotspot-explorer__content">
                <div className="hotspot-explorer__list-panel">
                    <div className="hotspot-explorer__list-panel-header">
                        <div className="hotspot-explorer__list-toolbar">
                            <ChecklistSearch
                                label="Search hotspots"
                                value={searchTerm}
                                placeholder="Search hotspots..."
                                onChange={setSearchTerm}
                            />
                            <div className="hotspot-explorer__sort-tabs" aria-label="Hotspot sort options">
                                <button
                                    type="button"
                                    className={`hotspot-explorer__sort-tab ${sortMode === 'visits' ? 'is-active' : ''}`}
                                    onClick={() => onChangeSort('visits')}
                                >
                                    Most Visited
                                </button>
                                <button
                                    type="button"
                                    className={`hotspot-explorer__sort-tab ${sortMode === 'species' ? 'is-active' : ''}`}
                                    onClick={() => onChangeSort('species')}
                                >
                                    Most Species
                                </button>
                                <button
                                    type="button"
                                    className={`hotspot-explorer__sort-tab ${sortMode === 'recent' ? 'is-active' : ''}`}
                                    onClick={() => onChangeSort('recent')}
                                >
                                    Most Recent
                                </button>
                            </div>
                        </div>
                        <span>
                            {filteredHotspots.length
                                ? `Showing ${startItem.toLocaleString()}-${endItem.toLocaleString()} of ${filteredHotspots.length.toLocaleString()} hotspots`
                                : 'No hotspots match your search'}
                        </span>
                    </div>
                    <HotspotTable hotspots={hotspotPage.items} onOpenHotspot={onOpenHotspot} />
                    <div className="hotspot-explorer__pagination" aria-label="Hotspot pagination">
                        <div className="hotspot-explorer__pagination-summary">
                            <span>
                                Page {hotspotPage.currentPage.toLocaleString()} of {totalPages.toLocaleString()}
                            </span>
                            <small>{filteredHotspots.length.toLocaleString()} hotspots total</small>
                        </div>
                        {showPaginationControls ? (
                            <nav className="hotspot-explorer__pagination-controls" aria-label="Page navigation">
                                <button
                                    type="button"
                                    className="hotspot-explorer__pagination-button"
                                    onClick={() => onPageChange(1)}
                                    disabled={hotspotPage.currentPage === 1}
                                >
                                    <FontAwesomeIcon icon={faAnglesLeft} aria-hidden="true" />
                                    <span>First</span>
                                </button>
                                <button
                                    type="button"
                                    className="hotspot-explorer__pagination-button"
                                    onClick={() => onPageChange(hotspotPage.currentPage - 1)}
                                    disabled={hotspotPage.currentPage === 1}
                                >
                                    <FontAwesomeIcon icon={faChevronLeft} aria-hidden="true" />
                                    <span>Prev</span>
                                </button>
                                <button
                                    type="button"
                                    className="hotspot-explorer__pagination-button"
                                    onClick={() => onPageChange(hotspotPage.currentPage + 1)}
                                    disabled={hotspotPage.currentPage === totalPages}
                                >
                                    <span>Next</span>
                                    <FontAwesomeIcon icon={faChevronRight} aria-hidden="true" />
                                </button>
                                <button
                                    type="button"
                                    className="hotspot-explorer__pagination-button"
                                    onClick={() => onPageChange(totalPages)}
                                    disabled={hotspotPage.currentPage === totalPages}
                                >
                                    <span>Last</span>
                                    <FontAwesomeIcon icon={faAnglesRight} aria-hidden="true" />
                                </button>
                            </nav>
                        ) : null}
                    </div>
                </div>

                <aside className="hotspot-explorer__panel" aria-label="Explore Your Places">
                    {groups.length ? (
                        <section className="hotspot-explorer__panel-section">
                            <h3 className="hotspot-explorer__panel-heading">{panelHeading}</h3>
                            <ul className="hotspot-explorer__group-list">
                                {groups.map(group => (
                                    <li key={group.code}>
                                        <button
                                            type="button"
                                            className={`hotspot-explorer__group-button ${activeGroupCode === group.code ? 'is-active' : ''}`}
                                            onClick={() => onSelectGroup(group.code)}
                                        >
                                            <span>{group.label}</span>
                                            <strong>{group.count.toLocaleString()}</strong>
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </section>
                    ) : (
                        <div className="hotspot-explorer__panel-empty">
                            <strong>No further subdivisions available</strong>
                            <span>This place only has hotspot-level data, so there is nothing else to drill into here.</span>
                        </div>
                    )}
                </aside>
            </div>
        </section>
    );
}
